/* eslint-disable camelcase */
import { useState, useRef } from "react";
import { Chakra } from "@airhelp/react-ui-components";

import config from "config/config";
import { registerUser } from "services/coral";

import { trackMixpanel, trackEvents } from "utils/trackingUtils";
import getPageUrl from "utils/getPageUrl";

import { useTranslations, useLanguage, useLocalizedData } from "contexts/LocalizationContext/hooks";
import { useUserContext } from "contexts/HelpAFriend/UserContext";
import { useReferralProgramConfig } from "contexts/DataContext/hooks";

import {
  MODAL_GENERATE_LINK_GTM,
  MODAL_GENERATE_LINK_MP,
  MODAL_REGISTER_SUCCESS_GTM,
  MODAL_REGISTER_SUCCESS_MP,
  MODAL_REGISTER_ERROR_GTM,
  MODAL_REGISTER_ERROR_MP,
} from "modules/help-a-friend/trackingConsts";

const { Button, Heading, Text, Input, Checkbox } = Chakra;

const NewUserView = () => {
  const translations = useTranslations();
  const { getLocalizedCurrency } = useLocalizedData();
  const language = useLanguage();
  const { updateShareLink, updateEmail, userEmail, setUserEmail } = useUserContext();

  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const emailInputRef = useRef(null);

  const termsTranslationVariables = {
    "%terms-link-attrs%": `href="${config.websiteUrl}${getPageUrl(
      language,
      "referralTerms"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "modal_terms_title"
    )}" style="text-decoration: underline"`,
    "%privacy-link-attrs%": `href="${config.websiteUrl}${getPageUrl(
      language,
      "privacy"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "modal_terms_privacy_title"
    )}" style="text-decoration: underline"`,
  };

  const referralProgramConfig = useReferralProgramConfig();
  const { campaignId, campaignSource, commissionValue } = referralProgramConfig;

  const handleEmailChange = (e) => {
    setUserEmail(e.target.value);
  };

  const trackRegisterSuccess = () => {
    if (window.dataLayer) {
      window.dataLayer.push(MODAL_REGISTER_SUCCESS_GTM);
    }
    trackMixpanel(MODAL_REGISTER_SUCCESS_MP);
  };

  const trackRegisterError = () => {
    if (window.dataLayer) {
      window.dataLayer.push(MODAL_REGISTER_ERROR_GTM);
    }
    trackMixpanel(MODAL_REGISTER_ERROR_MP);
  };

  const handleSubmit = () => {
    if (emailInputRef.current.reportValidity() && checked) {
      setIsLoading(true);

      trackEvents({ gtmEvent: MODAL_GENERATE_LINK_GTM, mixpanelEvent: MODAL_GENERATE_LINK_MP });

      registerUser(campaignId, language, userEmail, campaignSource)
        .then((response) => {
          const {
            data: { share_url },
          } = response;
          setIsLoading(false);
          updateShareLink(share_url);
          updateEmail(userEmail);
          trackRegisterSuccess();
        })
        .catch(() => {
          setIsLoading(false);
          setError(translations("modal_something_went_wrong"));
          trackRegisterError();
        });
    }
  };

  return (
    <>
      <Heading data-testid="modal-heading" variant="h3" as="h3" mb={3} maxW={{ base: "100%", md: "350px" }}>
        {translations("modal_help_friends_get_rewarded")}
      </Heading>
      <Text
        mb={10}
        dangerouslySetInnerHTML={{
          __html: translations("modal_enter_email", { "%commission%": getLocalizedCurrency(commissionValue, "EUR") }),
        }}
      />
      <Input
        data-testid="modal-input"
        required
        type="email"
        ref={emailInputRef}
        value={userEmail}
        onChange={handleEmailChange}
        mb={6}
        focusBorderColor="primary.500"
        placeholder={translations("enter_email_placeholder")}
      />
      <Checkbox
        data-testid="modal-checkbox"
        colorScheme="primary"
        mb={10}
        size="lg"
        onChange={(e) => setChecked(e.target.checked)}
      >
        <Text
          fontSize="sm"
          textAlign="start"
          dangerouslySetInnerHTML={{
            __html: translations("agree_to_terms", termsTranslationVariables),
          }}
        />
      </Checkbox>
      <Button
        data-testid="modal-generate-link"
        minWidth="100%"
        title={translations("modal_generate_link")}
        onClick={handleSubmit}
        disabled={!checked}
        isLoading={isLoading}
      >
        {translations("modal_generate_link")}
      </Button>
      {error && (
        <Text mt={4} color="danger.500">
          {error}
        </Text>
      )}
    </>
  );
};

export default NewUserView;
